import { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { es as esLocale } from "date-fns/locale";
import { ArrowUpRight, BadgePercent, CalendarX2, MoonStar } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";

function expandRanges(ranges) {
  const dates = [];
  for (const r of ranges || []) {
    const d = new Date(`${r.start}T12:00:00`);
    const end = new Date(`${r.end}T12:00:00`);
    let guard = 0;
    while (d < end && guard < 400) {
      dates.push(new Date(d));
      d.setDate(d.getDate() + 1);
      guard++;
    }
  }
  return dates;
}

export default function VillaAvailability({ onEnquire }) {
  const { t, lang } = useLang();
  const [villa, setVilla] = useState(null);
  const [months, setMonths] = useState(2);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 720px)");
    const update = () => setMonths(mq.matches ? 1 : 2);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    api.get("/villa").then((r) => setVilla(r.data)).catch(() => {});
  }, []);

  const blocked = useMemo(() => expandRanges(villa?.blocked), [villa]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-20 sm:pb-28" data-testid="availability-section">
      <Reveal>
        <div className="bg-white rounded-[2rem] border border-terracotta/10 shadow-[0_2px_24px_rgba(22,27,34,0.06)] p-6 sm:p-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <Caption>{t("availability.caption")}</Caption>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight mt-4 text-ink">
              {t("availability.titleA")} <span className="italic text-terracotta">{t("availability.titleB")}</span>
            </h2>
            <div className="flex items-end gap-2 mt-8">
              <span className="font-serif text-6xl text-terracotta leading-none" data-testid="villa-nightly-rate">
                ${villa?.nightly_rate ?? "—"}
              </span>
              <span className="text-ink/50 mb-1.5">{t("availability.perNight")}</span>
            </div>
            <ul className="mt-8 space-y-3 text-sm text-ink/65">
              <li className="flex items-center gap-3">
                <MoonStar size={16} className="text-teal shrink-0" /> {villa?.min_nights === 1 ? t("availability.minStay1") : t("availability.minStay", { n: villa?.min_nights ?? "…" })}
              </li>
              <li className="flex items-center gap-3">
                <BadgePercent size={16} className="text-teal shrink-0" /> {t("availability.depositLine", { pct: villa?.deposit_percent ?? "…" })}
              </li>
              <li className="flex items-center gap-3">
                <CalendarX2 size={16} className="text-terracotta shrink-0" /> {t("availability.crossedOut")}
              </li>
            </ul>
            <button
              data-testid="availability-enquire-btn"
              onClick={onEnquire}
              className="mt-10 rounded-full bg-terracotta text-sand px-7 py-3.5 text-sm font-semibold hover:bg-terracotta-dark transition-colors inline-flex items-center gap-2"
            >
              {t("availability.cta")} <ArrowUpRight size={16} />
            </button>
          </div>
          <div className="flex justify-center overflow-x-auto" data-testid="availability-calendar">
            <DayPicker
              numberOfMonths={months}
              showOutsideDays={false}
              locale={lang === "es" ? esLocale : undefined}
              modifiers={{ blocked }}
              modifiersClassNames={{ blocked: "vp-blocked" }}
              disabled={[{ before: new Date() }, ...blocked]}
            />
            <style>{`
              .vp-blocked { text-decoration: line-through; color: #B83F12 !important; opacity: 0.45; }
              .rdp { --rdp-cell-size: 32px; --rdp-accent-color: #D9531E; --rdp-background-color: #FEF3C7; margin: 0; }
              .rdp-day_selected { background: #D9531E; color: #FAF7F2; }
              .rdp-caption_label, .rdp-head_cell { font-family: 'Outfit', sans-serif; }
              .rdp-caption_label { font-family: 'Cormorant Garamond', serif; font-size: 1.15rem; }
              .rdp-head_cell { font-size: 0.65rem; }
              .rdp-day { font-size: 0.8rem; }
            `}</style>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
